import i18n, { loadNamespace } from "../i18n";

const LOCALE_MAP = {
  pl: "pl_PL",
  en: "en_US",
  uk: "uk_UA",
  de: "de_DE",
  it: "it_IT",
  sk: "sk_SK",
  es: "es_ES",
  fr: "fr_FR",
};

const SEO_DESCRIPTION_SUFFIX = {
  pl: "Poznaj narzędzia MediaDock dla produkcji live, ekip broadcastowych i freelancerów.",
  en: "Explore MediaDock tools for live production teams, broadcasters, and freelancers.",
  uk: "Дізнайтеся про MediaDock для live-продакшену, broadcast-команд і фрилансерів.",
  de: "Entdecken Sie MediaDock für Live-Produktion, Broadcast-Teams und Freelancer.",
  it: "Scopri MediaDock per produzioni live, team broadcast e freelance.",
  sk: "Objavte MediaDock pre live produkciu, broadcastové tímy a freelancerov.",
  es: "Descubre MediaDock para producción en directo, equipos broadcast y freelancers.",
  fr: "Découvrez MediaDock pour la production live, les équipes broadcast et les freelances.",
};

function normalizeLang(raw) {
  if (!raw) return "";
  return String(raw).toLowerCase().split("-")[0];
}

function resolveLang(langOverride) {
  const fromOverride = normalizeLang(langOverride || "");
  if (fromOverride) return fromOverride;
  try {
    const fromUrl = normalizeLang(
      new URL(window.location.href).searchParams.get("lang") || "",
    );
    if (fromUrl) return fromUrl;
  } catch (e) {}
  return normalizeLang((i18n && i18n.language) || "") || getDefaultLang();
}

function toAbsoluteUrl(href) {
  const base =
    typeof location !== "undefined" && location.origin
      ? location.origin
      : "https://mediadock.tv";
  return new URL(href || "/", base);
}

function withLangParam(href, langCode) {
  try {
    const url = toAbsoluteUrl(href);
    const lang = normalizeLang(langCode || "");
    url.hash = "";
    if (lang && lang !== getDefaultLang()) {
      url.searchParams.set("lang", lang);
    } else {
      url.searchParams.delete("lang");
    }
    return url.toString();
  } catch (e) {
    return href;
  }
}

function getDefaultLang() {
  const fallback = i18n && i18n.options ? i18n.options.fallbackLng : null;
  if (Array.isArray(fallback) && fallback.length)
    return normalizeLang(fallback[0]) || "pl";
  if (typeof fallback === "string") return normalizeLang(fallback) || "pl";
  return "pl";
}

function buildLocalizedCanonical(href, langOverride) {
  if (!href) return href;
  return withLangParam(href, resolveLang(langOverride));
}

function getMeta(attr, key) {
  try {
    return document.querySelector(`meta[${attr}="${key}"]`);
  } catch (e) {
    return null;
  }
}

function setMetaContent(attr, key, value, scope) {
  if (!key) return;
  try {
    let el = getMeta(attr, key);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    if (scope) {
      el.setAttribute("data-generated", scope);
    }
    el.setAttribute("content", value || "");
  } catch (e) {}
}

function isPageScoped(attr, key) {
  try {
    const el = getMeta(attr, key);
    return !!(el && el.getAttribute("data-generated") === "page");
  } catch (e) {
    return false;
  }
}

export function stripHtmlText(value = "") {
  try {
    return String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  } catch (e) {
    return String(value || "");
  }
}

export function summarizeText(value = "", max = 160) {
  const txt = stripHtmlText(value);
  if (!txt) return "";
  if (txt.length <= max) return txt;
  const sliced = txt.slice(0, Math.max(0, max - 3)).replace(/\s+\S*$/, "");
  return (sliced || txt.slice(0, max - 3)) + "...";
}

export function normalizeMetaDescription(value = "", langOverride) {
  const normalized = stripHtmlText(value);
  if (!normalized) return "";
  const lang = resolveLang(langOverride);
  const suffix = SEO_DESCRIPTION_SUFFIX[lang] || SEO_DESCRIPTION_SUFFIX.pl;
  const expanded =
    normalized.length < 120 ? `${normalized} ${suffix}` : normalized;
  return summarizeText(expanded, 160);
}

export function setCanonical(href, langOverride) {
  try {
    const localized = buildLocalizedCanonical(href, langOverride);
    const canonicalEl =
      document.querySelector('link[rel="canonical"]') ||
      document.createElement("link");
    canonicalEl.setAttribute("rel", "canonical");
    canonicalEl.href = localized;
    if (!document.querySelector('link[rel="canonical"]'))
      document.head.appendChild(canonicalEl);
    return localized;
  } catch (e) {
    return href;
  }
}

export function addHreflangVariants(
  basePath,
  langCodes = [],
  defaultLang = "pl",
) {
  try {
    Array.from(
      document.querySelectorAll('link[data-generated="hreflang"]'),
    ).forEach((el) => el.remove());

    if (typeof window === "undefined") return;

    const currentUrl = new URL(window.location.href);
    const canonicalUrl = new URL(buildLocalizedCanonical(basePath));
    const isLocalOrigin = ["localhost", "127.0.0.1", "0.0.0.0"].includes(
      currentUrl.hostname,
    );
    const sameOrigin =
      isLocalOrigin || currentUrl.origin === canonicalUrl.origin;
    const normalizePath = (pathname) => pathname.replace(/\/+$/, "") || "/";
    const isCanonicalUrl =
      sameOrigin &&
      normalizePath(currentUrl.pathname) ===
        normalizePath(canonicalUrl.pathname) &&
      currentUrl.search === canonicalUrl.search;

    if (!isCanonicalUrl) return;

    const effectiveDefault = normalizeLang(defaultLang) || getDefaultLang();
    const normalizedLangs = Array.from(
      new Set((langCodes || []).map(normalizeLang).filter(Boolean)),
    );
    const langs = normalizedLangs.length ? normalizedLangs : [effectiveDefault];
    const baseUrl = toAbsoluteUrl(
      basePath || (typeof window !== "undefined" ? window.location.href : "/"),
    );
    baseUrl.hash = "";
    baseUrl.searchParams.delete("lang");

    langs.forEach((l) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", l);
      const href = withLangParam(baseUrl.toString(), l);
      link.setAttribute("href", href);
      link.setAttribute("data-generated", "hreflang");
      document.head.appendChild(link);
    });

    const xdef = document.createElement("link");
    xdef.setAttribute("rel", "alternate");
    xdef.setAttribute("hreflang", "x-default");
    xdef.setAttribute(
      "href",
      withLangParam(baseUrl.toString(), effectiveDefault),
    );
    xdef.setAttribute("data-generated", "hreflang");
    document.head.appendChild(xdef);
  } catch (e) {}
}

export function clearHreflang() {
  try {
    Array.from(
      document.querySelectorAll('link[data-generated="hreflang"]'),
    ).forEach((el) => el.remove());
  } catch (e) {}
}

export function setPageMeta({
  title,
  description,
  keywords,
  image,
  canonical,
  robots,
} = {}) {
  try {
    if (title) {
      const rawTitle = String(title).replace(/\s+/g, " ").trim();
      const titleSuffix = " - MediaDock";
      const normalizedTitle = /mediadock/i.test(rawTitle)
        ? rawTitle
        : `${rawTitle}${titleSuffix}`;
      const safeTitle =
        normalizedTitle.length > 60
          ? `${normalizedTitle.slice(0, 57).replace(/\s+\S*$/, "")}…`
          : normalizedTitle;
      document.title = safeTitle;
      title = safeTitle;
    }

    if (typeof description !== "undefined") {
      const safeDescription = normalizeMetaDescription(description);
      setMetaContent("name", "description", safeDescription, "page");
      setMetaContent("property", "og:description", safeDescription, "page");
      setMetaContent("name", "twitter:description", safeDescription, "page");
    }

    if (typeof keywords !== "undefined") {
      const value = Array.isArray(keywords)
        ? keywords.join(", ")
        : keywords || "";
      setMetaContent("name", "keywords", value, "page");
    }

    if (title) {
      setMetaContent("property", "og:title", title, "page");
      setMetaContent("name", "twitter:title", title, "page");
    }

    if (image) {
      setMetaContent("property", "og:image", image, "page");
      setMetaContent("name", "twitter:image", image, "page");
    }

    if (canonical) {
      const resolvedCanonical = setCanonical(canonical);
      setMetaContent(
        "property",
        "og:url",
        resolvedCanonical || canonical,
        "page",
      );
    }

    const effectiveRobots =
      typeof robots !== "undefined" ? robots || "" : "index, follow";
    setMetaContent("name", "robots", effectiveRobots, "page");
  } catch (e) {}
}

export function clearMeta() {
  try {
    Array.from(
      document.querySelectorAll('meta[data-generated="page"]'),
    ).forEach((el) => el.remove());
  } catch (e) {}
}

export function setStructuredData(key, data, scope = "page") {
  try {
    if (!key || !data) return;
    let el = document.querySelector(
      `script[type="application/ld+json"][data-jsonld-key="${key}"]`,
    );
    if (!el) {
      el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("data-jsonld-key", key);
      document.head.appendChild(el);
    }
    if (scope) {
      el.setAttribute("data-generated", scope);
    }
    el.textContent = JSON.stringify(data);
  } catch (e) {}
}

export function clearStructuredData(scope = "page") {
  try {
    Array.from(
      document.querySelectorAll(
        `script[type="application/ld+json"][data-generated="${scope}"]`,
      ),
    ).forEach((el) => el.remove());
  } catch (e) {}
}

function setDocumentLanguage(lang) {
  try {
    if (!lang) return;
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.lang = lang;
    }
  } catch (e) {}
}

function setOgLocales(activeLang, allLangs) {
  try {
    const locale = LOCALE_MAP[activeLang] || activeLang;
    setMetaContent("property", "og:locale", locale, "meta");
    Array.from(
      document.querySelectorAll(
        'meta[property="og:locale:alternate"][data-generated="meta"]',
      ),
    ).forEach((el) => el.remove());
    const alternates = (allLangs || [])
      .map((l) => LOCALE_MAP[l] || l)
      .filter((l) => l && l !== locale);
    alternates.forEach((l) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "og:locale:alternate");
      el.setAttribute("content", l);
      el.setAttribute("data-generated", "meta");
      document.head.appendChild(el);
    });
  } catch (e) {}
}

export async function setLocalizedMeta(lang) {
  try {
    if (!lang) lang = i18n.language;
    lang = resolveLang(lang);
    await loadNamespace("meta", lang);
    setDocumentLanguage(lang);

    const description =
      i18n.t("description", { ns: "meta", lng: lang, defaultValue: "" }) || "";
    const keywords =
      i18n.t("keywords", { ns: "meta", lng: lang, defaultValue: "" }) || "";
    const ogDescription =
      i18n.t("ogDescription", { ns: "meta", lng: lang, defaultValue: "" }) ||
      description;
    const twitterDescription =
      i18n.t("twitterDescription", {
        ns: "meta",
        lng: lang,
        defaultValue: "",
      }) || ogDescription;

    if (!isPageScoped("name", "description")) {
      setMetaContent("name", "description", description || "", "meta");
    }
    if (!isPageScoped("name", "keywords")) {
      const kw = Array.isArray(keywords) ? keywords.join(", ") : keywords || "";
      setMetaContent("name", "keywords", kw, "meta");
    }
    if (!isPageScoped("property", "og:description")) {
      setMetaContent("property", "og:description", ogDescription, "meta");
    }
    if (!isPageScoped("name", "twitter:description")) {
      setMetaContent("name", "twitter:description", twitterDescription, "meta");
    }

    try {
      let gurl = location.origin + (location.pathname || "/");
      gurl = gurl.replace(/\/+$/, "/");
      const localizedCanonical = setCanonical(gurl, lang);
      if (!isPageScoped("property", "og:url")) {
        setMetaContent(
          "property",
          "og:url",
          localizedCanonical || gurl,
          "meta",
        );
      }

      const langs = ["pl", "en", "uk", "de", "it", "sk", "es", "fr"];
      const base = location.origin + (location.pathname || "/");
      addHreflangVariants(base, langs);
      setOgLocales(lang, langs);
    } catch (e) {}
  } catch (e) {
    /* fail silently */
  }
}
